import { computed } from 'vue';
import {
  computeCmgPayable,
  computeCmgEligibleAmount,
  computeGrossMensualization,
  computeNetMensualization,
} from '../calculations/cmgCalculations';
import { computeTotalContributions } from '../calculations/socialContributions';
import {
  GED_EFFORT_RATE_HOURLY,
  GED_HOURLY_WAGE_REFERENCE,
  computeEmployerContributionsReductionCost,
  computeEmployerContributionsReduction,
  computeGedSocialContributionsCredit,
  computeGedCreditTaxCeil,
  computeGedTaxesCredit,
  computeGedAmountLeftPayableBeforeTaxes,
  computeGedAmountLeftPayable,
  computeGedTotalAmount,
} from '../calculations/cmgCalculationsGed';

const CARE_TYPE = 'ged';

/**
 * Financial calculations for the GED CMG simulator.
 */
export function useCmgCalculationsGed(params) {
  const grossMensualization = computed(() => computeGrossMensualization({
    grossMonthlyWageRegular: params.grossMonthlyWageRegular.value,
    grossMonthlyOvertimeWage: params.grossMonthlyOvertimeWage.value,
  }));

  const netMensualization = computed(() => computeNetMensualization({
    grossMonthlyWageRegular: params.grossMonthlyWageRegular.value,
    netRatioClassic: params.netRatioClassic.value,
    grossMonthlyOvertimeWage: params.grossMonthlyOvertimeWage.value,
    netRatioDetax: params.netRatioDetax.value,
    hasOvertime: params.hasOvertime.value || params.hasOvertimeFifty.value,
  }));

  const netMensualizationClassic = computed(() => params.grossMonthlyWageRegular.value * params.netRatioClassic.value);
  const netMensualizationDetax = computed(() => {
    return (params.hasOvertime.value || params.hasOvertimeFifty.value)
      ? (params.grossMonthlyOvertimeWage.value * params.netRatioDetax.value)
      : 0;
  });
  const netMonthlyOvertimeIncrease = computed(() => params.grossMonthlyOvertimeWagePremium.value * params.netRatioDetax.value);

  const grossTotal = computed(() => grossMensualization.value + params.grossMonthlyOvertimeWagePremium.value);

  const netVacationsMonthly = computed(() => 0);
  const grossVacationsMonthly = computed(() => 0);

  const socialContributionsDetaxBase = computed(() => params.grossMonthlyOvertimeWage.value + params.grossMonthlyOvertimeWagePremium.value);

  const socialContributionsEmployee = computed(() => {
    return computeTotalContributions(
      params.socialContributions.value,
      'employee',
      grossTotal.value,
      socialContributionsDetaxBase.value,
      params.region.value,
      CARE_TYPE,
    );
  });

  const socialContributionsEmployer = computed(() => {
    return computeTotalContributions(
      params.socialContributions.value,
      'employer',
      grossTotal.value,
      socialContributionsDetaxBase.value,
      params.region.value,
      CARE_TYPE,
    );
  });

  const employerContributionsReductionCost = computed(() => computeEmployerContributionsReductionCost(params.region.value));

  const employerContributionsReduction = computed(() => computeEmployerContributionsReduction({
    regularHours: params.familyRegularHours.value,
    monthlyOvertimeFloat: params.familyOvertimeHours.value,
    employerContributionsReductionCost: employerContributionsReductionCost.value,
    socialContributionsEmployer: socialContributionsEmployer.value,
    socialContributionsEmployee: socialContributionsEmployee.value,
  }));

  const totalAmount = computed(() => computeGedTotalAmount({
    grossTotal: grossTotal.value,
    socialContributionsEmployer: socialContributionsEmployer.value,
  }));

  const netPaidTotal = computed(() => netMensualization.value + netMonthlyOvertimeIncrease.value);

  const realMonthlyRateNet = computed(() => netMensualizationClassic.value + netMonthlyOvertimeIncrease.value);
  const realHourlyRateNet = computed(() => (params.familyMonthlyHours.value > 0
    ? realMonthlyRateNet.value / params.familyMonthlyHours.value
    : 0));
  const maxRealHourlyRate = computed(() => Math.min(realHourlyRateNet.value, params.maxNetHelp.value));
  const maxCMGHelpExceeded = computed(() => realHourlyRateNet.value > params.maxNetHelp.value);

  const socialContributionsCredit = computed(() => computeGedSocialContributionsCredit({
    cmgUnavailable: params.cmgUnavailable.value,
    socialContributionsEmployer: socialContributionsEmployer.value,
    socialContributionsEmployee: socialContributionsEmployee.value,
    childAge: params.childAge.value,
  }));

  const creditTaxCeil = computed(() => computeGedCreditTaxCeil({
    children: params.childrenI.value,
    firstEmployeeDeclaration: params.firstEmployeeDeclaration.value,
  }));

  function cmgPayable(parentMonthlySalaryNet) {
    return computeCmgPayable({
      maxRealHourlyRate: maxRealHourlyRate.value,
      monthlyHours: params.familyMonthlyHours.value,
      parentMonthlySalaryNet,
      effortRate: GED_EFFORT_RATE_HOURLY[params.childrenI.value],
      hourlyWageReference: GED_HOURLY_WAGE_REFERENCE,
    });
  }

  function cmgEligibleAmount(parentMonthlySalaryNet) {
    return computeCmgEligibleAmount({
      cmgPayable: cmgPayable(parentMonthlySalaryNet),
      cmgNotAvailable: params.cmgNotAvailable.value,
      handicapToggle: params.handicapToggle.value,
      cmgAidToggle: params.cmgAidToggle.value,
      parentMonthlySalaryNet,
    });
  }

  function taxesCredit(parentMonthlySalaryNet) {
    return computeGedTaxesCredit({
      grossTotal: grossTotal.value,
      socialContributionsEmployer: socialContributionsEmployer.value,
      employerContributionsReduction: employerContributionsReduction.value,
      socialContributionsCredit: socialContributionsCredit.value,
      cmgEligibleAmount: cmgEligibleAmount(parentMonthlySalaryNet),
      otherAid: params.otherAidF.value,
      creditTaxCeil: creditTaxCeil.value,
    });
  }

  function amountLeftPayable(parentMonthlySalaryNet) {
    return computeGedAmountLeftPayable({
      amountLeftPayableBeforeTaxes: amountLeftPayableBeforeTaxes(parentMonthlySalaryNet),
      taxesCredit: taxesCredit(parentMonthlySalaryNet),
    });
  }

  function amountLeftPayableBeforeTaxes(parentMonthlySalaryNet) {
    return computeGedAmountLeftPayableBeforeTaxes({
      totalAmount: totalAmount.value,
      socialContributionsCredit: socialContributionsCredit.value,
      employerContributionsReduction: employerContributionsReduction.value,
      cmgEligibleAmount: cmgEligibleAmount(parentMonthlySalaryNet),
      otherAid: params.otherAidF.value,
    });
  }

  function dailyCost(parentMonthlySalaryNet) {
    return amountLeftPayable(parentMonthlySalaryNet) / params.monthlyWorkedDaysF.value;
  }

  function hourlyCost(parentMonthlySalaryNet) {
    return amountLeftPayable(parentMonthlySalaryNet) / params.monthlyHoursF.value;
  }

  return {
    grossMensualization,
    netMensualization,
    netMensualizationClassic,
    netMensualizationDetax,
    netMonthlyOvertimeIncrease,
    grossTotal,
    netVacationsMonthly,
    grossVacationsMonthly,
    socialContributionsEmployee,
    socialContributionsEmployer,
    employerContributionsReduction,
    socialContributionsCredit,
    creditTaxCeil,
    totalAmount,
    netPaidTotal,
    realMonthlyRateNet,
    realHourlyRateNet,
    maxRealHourlyRate,
    maxCMGHelpExceeded,
    cmgPayable,
    cmgEligibleAmount,
    taxesCredit,
    amountLeftPayable,
    amountLeftPayableBeforeTaxes,
    dailyCost,
    hourlyCost,
  };
}
